import { DelegatedSessions } from '@/api/oversight/types';

type formatDateProps = 'absolute' | 'relative';

type Item = {
  value: string;
  label: string;
  active?: boolean;
};

type Filter = {
  id: number;
  label: string;
  paramKey: string;
  isActive: boolean;
  disabled?: boolean;
  items: Item[];
};

function formatDate(isoString: string, format: formatDateProps): string {
  const date = new Date(isoString);
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 1000 / 60);
  const diffHours = Math.floor(diffMin / 60);

  const sameDay = (d1: Date, d2: Date): boolean =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const hour = date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const formatedDate = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  if (format === 'absolute') {
    if (sameDay(date, now)) return `Hoje • ${hour}`;
    if (sameDay(date, yesterday)) return `Ontem • ${hour}`;
    return `${formatedDate} • ${hour}`;
  }

  if (format === 'relative') {
    if (diffMin < 1) return 'Agora';
    if (diffMin < 60) return `Há ${diffMin} min`;
    if (sameDay(date, now)) return `Há ${diffHours}h`;
    if (sameDay(date, yesterday)) return `Ontem • ${hour}`;
    return `Último • ${formatedDate}`;
  }

  return formatedDate;
}

const normalizeStr = (str: string): string =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

// Filtro temporário: a API ainda lista os usuários apagados no app.
const deletedUserPattern = /usuario[\s_-]*excluido/;

function isDeletedUser(...fields: (string | null | undefined)[]): boolean {
  return fields.some(
    (field) => !!field && deletedUserPattern.test(normalizeStr(field))
  );
}

const delegatedAccessLabels: Record<string, string> = {
  pending_consent: 'Consentimento pendente',
  approved: 'Solicita\u00e7\u00e3o aprovada',
  active: 'Sess\u00e3o delegada ativa',
  ended: 'Sess\u00e3o encerrada',
  rejected: 'Solicita\u00e7\u00e3o negada',
  expired: 'Solicita\u00e7\u00e3o expirada',
  canceled: 'Solicita\u00e7\u00e3o cancelada',
  revoked: 'Revogado',
};

const endReasonLabel: Record<string, string> = {
  manual: 'Encerrada manualmente',
  timeout: 'Tempo limite atingido',
  inactivity: 'Encerrada por inatividade',
  approval_expired: 'Prazo de início atingido',
  security_event: 'Encerrada pelo sistema',
  client_terminated: 'Encerrada pelo cliente',
};

const methodVerb: Record<string, string> = {
  GET: 'Visualizou',
  POST: 'Criou',
  PUT: 'Atualizou',
  PATCH: 'Atualizou',
  DELETE: 'Removeu',
};

const resourceLabels: Record<string, string> = {
  plans: 'planos',
  credits: 'cr\u00e9ditos',
  companies: 'dados da empresa',
  oversight: 'acesso delegado',
  users: 'usu\u00e1rios',
  notifications: 'notifica\u00e7\u00f5es',
  invoices: 'faturas',
  inspections: 'vistorias',
  samples: 'amostras',
  'work-orders': 'OS',
  'public-notices': 'credenciamentos',
  pictures: 'fotos',
  rooms: 'c\u00f4modos',
  attachments: 'anexos',
  forms: 'formul\u00e1rios',
  'hive-messages': 'mensagens do Hive',
  'notice-companies': 'credenciamento da empresa',
  otp: 'verificação por código',
  totp: 'autenticação em dois fatores',
  'calendar-suggestions': 'sugestões de agenda',
  'calendar-blockages': 'bloqueios de agenda',
  'calendar-schedules': 'agendamentos',
  'error-reports': 'erros reportados',
  'report-certifiers': 'validadores de laudo',
  'reset-password': 'redefinição de senha',
  announcements: 'comunicados',
  'notice-announcements': 'comunicados de edital',
  avm: 'análise AVM',
  'licensed-companies': 'empresas credenciadas',
  'licensed-company-absences': 'ausências da empresa',
  pathologies: 'patologias',
  'proto-mobile': 'aplicativo',
  'report-customizations': 'personalização de laudo',
  academy: 'Academy',
  cities: 'cidades',
  coupons: 'cupons',
  hubspot: 'atendimento',
  'maintenance-notifications': 'avisos de manutenção',
  'nps-forms': 'pesquisa NPS',
  resource_purchases: 'compras de recursos',
};

// Session-related HTTPException subclasses raised by the API (app/base/errors/exceptions.py).
// error_type in event_metadata is always the raw exception class name (type(exc).__name__),
// so unmapped/unknown values fall back to the raw string instead of breaking the render.
const errorTypeLabels: Record<string, string> = {
  HTTP400Exception: 'Requisição inválida',
  HTTP401Exception: 'Sessão inválida',
  HTTP403Exception: 'Acesso não autorizado',
  HTTP404Exception: 'Sessão não encontrada',
  HTTP409Exception: 'Conflito de sessão',
  HTTP500Exception: 'Erro interno',
};

function getErrorTypeTitle(errorType?: string): string {
  if (!errorType) return 'Erro desconhecido';
  return errorTypeLabels[errorType] ?? errorType;
}

function expiredDetail(
  row: DelegatedSessions
): { label: string; tooltip: string } | null {
  if (row.status !== 'expired') {
    return null;
  }

  if (row.end_reason === 'approval_expired') {
    return {
      label: 'Prazo de início atingido',
      tooltip: 'Supervisor não iniciou o acesso delegado no prazo de 72 horas.',
    };
  }

  if (!row.consent_responded_at) {
    return {
      label: 'Prazo de resposta atingido',
      tooltip: 'Cliente não respondeu a solicitação no prazo de 24 horas.',
    };
  }

  return null;
}

const terminatedByLabels: Record<string, string> = {
  'support:user_id': 'Sessão encerrada pelo suporte',
  'system:inactivity': 'Sessão encerrada por inatividade',
  'user:logout': 'Sessão encerrada pelo usuário',
  'system:superseded': 'Sessão encerrada por novo login',
};

function getTerminatedByLabel(terminatedBy?: string): string {
  if (!terminatedBy) return 'Sessão encerrada';
  return terminatedByLabels[terminatedBy] ?? 'Sessão encerrada';
}

// Not every `event_type` comes from the app: the backend emits its own for
// things no request triggers, such as a session being replaced by a newer one.
// Those keys carry no route half and are matched here by their bare name.
const routeGroupLabels: Record<string, string> = {
  session_superseded: 'Sessão encerrada por novo login',
  background: 'Atividade automática',
  bootstrap: 'Carregamento do app',
  login: 'Login',
  logout: 'Logout',
  home: 'Início',
  'home/property/new': 'Nova OS',
  'home/property/{id}/edit': 'Edição da OS',
  'home/property/{id}/schedule': 'Agendamento',
  'home/property/{id}/inspection': 'Vistoria',
  'home/property/{id}/sample': 'Criação de amostra',
  'home/property/{id}/calculation': 'Processando cálculo',
  'home/property/{id}/report': 'Elaboração de laudo',
  'home/property/{id}/revision': 'Revisão e aprovação',
  'home/property/{id}/pept': 'Pendência técnica',
  'home/property/{id}/archived': 'OS arquivada',
  'home/presentation-mode': 'Modo apresentação',
  // Telas do proto-front: chegam por Acesso Delegado, no mesmo formato
  // `<grupo>:<rota>` com `{id}`, porque aquele app envia X-Event-Type com a
  // mesma normalização de rota.
  academy: 'Academy',
  accreditation: 'Credenciamento',
  consent: 'Consentimento de acesso delegado',
  'expired-token': 'Link expirado',
  'first-access': 'Primeiro acesso',
  'inspection-confirmation': 'Confirmação de vistoria',
  'monitored-inspection': 'Vistoria virtual monitorada',
  'open-in-app': 'Abrir no aplicativo',
  'pre-inspection': 'Pré-vistoria',
  'recover-password': 'Recuperar senha',
  'report-validation': 'Validação de laudo',
  satisfaction: 'Pesquisa de satisfação',
  tools: 'Ferramentas',
  validation: 'Validação',
  'virtual-inspection': 'Vistoria virtual',

  // Telas anteriores ao login deste app: não aparecem no menu, de onde saiu o
  // resto deste mapa.
  'create-account': 'Criação de conta',
  'forgot-password': 'Recuperar senha',
  'new-password': 'Redefinição de senha',
  'reset-password': 'Redefinição de senha',

  adjustment: 'Ajustes',
  agenda: 'Agenda',
  announcements: 'Comunicados',
  archived: 'Arquivados',
  configuration: 'Configurações',
  dashboard: 'Dashboard',
  'error-report': 'Erros reportados',
  finance: 'Financeiro',
  forms: 'Formulários',
  notifications: 'Notificações',
  oversight: 'Oversight',
  'oversight/clients': 'Clientes do Oversight',
  'oversight/administrative-users': 'Usuários administrativos',
  'oversight/clients/customer-details/{id}': 'Detalhes do cliente',
  'oversight/administrative-users/details/{id}':
    'Detalhes do usuário administrativo',
  'pre-flight': 'Pre flight',
  // Guarda-chuva: só pega as telas do Hive que ninguém mapeou ainda, para uma
  // tela nova aparecer como si mesma em vez de entrar em Pedidos por engano.
  // As abas seguem o nome que o usuário lê em HIVE_TABS.
  'the-hive': 'The Hive',
  'the-hive/requests': 'Pedidos do Hive',
  'the-hive/licensed': 'Credenciados do Hive',
  'the-hive/licensed/{id}': 'Detalhes do edital',
  'the-hive/licensed/company-details/{id}': 'Detalhes do credenciado',
  // O mesmo rótulo em duas chaves é proposital: a tela é a mesma, alcançada por
  // Credenciados e por Editais, e o agrupamento colapsa por rótulo.
  'the-hive/licensed/company-details/{id}/notices/{id}/request/{id}':
    'Análise de pedido de credenciamento',
  'the-hive/notices': 'Editais',
  'the-hive/notices/new': 'Novo edital',
  'the-hive/notices/{id}': 'Detalhes do edital',
  'the-hive/notices/{id}/add-company': 'Inclusão de empresa no edital',
  'the-hive/notices/{id}/edit': 'Edição de edital',
  'the-hive/notices/{id}/request/{id}': 'Análise de pedido de credenciamento',
  // As 10 telas da Rede Proto são abas de um mesmo painel: percorrê-las é uma
  // atividade só, e cada evento dentro do grupo continua descrito por endpoint.
  'the-hive/rede-proto': 'Dashboard credenciado',
  'user-management': 'Gestão de usuários',
  users: 'Usuários',
  'users-roles': 'Perfis de acesso',
};

// Route groups keep their `{id}` placeholders, so `home/property/{id}/report` and
// `home/property/{id}/report/characteristics` are the same screen to the reader.
// Longest prefix wins; an unmapped group falls back to its raw key so a new
// screen shows up as itself instead of silently merging into another one.
function describeRouteGroup(groupKey: string): string {
  const exact = routeGroupLabels[groupKey];
  if (exact) return exact;

  const [longestPrefix] = Object.keys(routeGroupLabels)
    .filter((candidate) => groupKey.startsWith(`${candidate}/`))
    .sort((a, b) => b.length - a.length);

  return longestPrefix ? routeGroupLabels[longestPrefix] : groupKey;
}

// Credenciamento. Consultado antes de `/work-orders` porque os endpoints de
// edital carregam `/licensed-companies/{id}/work-orders`, que senão seria lido
// como a lista de OS da empresa. `/me/public-notices` é a tela de Credenciamento
// do proto: a empresa olhando os próprios credenciamentos.
function describePublicNoticeEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('public-notices')) return undefined;

  if (path.includes('/me/public-notices'))
    return 'Visualizou os próprios editais';
  if (path.includes('/available'))
    return 'Visualizou novos editais disponíveis';
  if (path.includes('/discredit')) return 'Descredenciou uma empresa';
  if (path.includes('/acceptance-documents')) {
    return method === 'POST'
      ? 'Enviou documentos do credenciamento'
      : 'Visualizou os documentos do credenciamento';
  }
  if (path.includes('/work-orders')) return 'Visualizou as OS do edital';
  if (path.includes('/payments')) return 'Visualizou os pagamentos do edital';
  if (path.includes('/announcements'))
    return 'Visualizou os comunicados do edital';
  if (path.includes('/attachments')) return 'Visualizou os anexos do edital';

  const noticeMatch = path.match(/public-notices\/(\d+)/);
  if (noticeMatch) return `Abriu o edital #${noticeMatch[1]}`;

  return 'Visualizou credenciamentos';
}

// Split by domain to stay under the complexity ceiling, and because each of
// these families has its own list-versus-detail traps. Each returns undefined
// when it does not recognise the path, so the caller keeps falling through in
// the original order — that order is load-bearing.
function describeAuthEvent(method: string, path: string): string | undefined {
  if (path.includes('/logout')) return 'Fez logout';

  // The Oversight token is swapped every 15min and the theme is fetched on
  // mount; both hit paths containing /auth/ or /login. Only POST /login is a
  // real login.
  if (path.includes('/auth/exchange')) return 'Renovou a sessão';

  if (path.includes('/login') || path.includes('/auth/')) {
    return method === 'POST'
      ? 'Login realizado'
      : 'Carregou as configurações da empresa';
  }

  return undefined;
}

function describeWorkOrderEvent(
  method: string,
  path: string
): string | undefined {
  if (!path.includes('/work-orders')) return undefined;

  if (path.includes('/approve')) return 'Aprovou o resultado';

  if (
    path.includes('/reject') ||
    path.includes('/previous-status') ||
    path.includes('/pept')
  ) {
    return 'Recusou o resultado';
  }

  if (path.includes('/report')) return 'Acessou o laudo';
  if (path.includes('/samples')) return 'Visualizou as amostras da OS';

  const idMatch = path.match(/work-orders\/(\d+)/);
  if (idMatch) return `Abriu a OS #${idMatch[1]}`;

  if (method === 'POST') return 'Criou uma OS';

  return 'Visualizou a lista de OS';
}

// `inspection-forms` are the saved forms and templates, not inspections. The
// leading slash keeps `/pre-inspection-forms` out of this branch.
function describeFormEvent(method: string, path: string): string | undefined {
  if (!path.includes('/inspection-forms')) return undefined;

  if (method === 'POST') {
    return path.includes('as_template=true')
      ? 'Criou um modelo de formulário'
      : 'Criou um formulário';
  }

  if (method === 'PUT' || method === 'PATCH') return 'Atualizou um formulário';
  if (method === 'DELETE') return 'Removeu um formulário';
  if (/\/inspection-forms\/\d+/.test(path)) return 'Abriu um formulário';

  return path.includes('templates=true')
    ? 'Visualizou os modelos de formulário'
    : 'Visualizou formulários salvos';
}

function describeOversightEvent(
  method: string,
  path: string
): string | undefined {
  // `/clients` serves the list, one client, and that client's companies, so
  // the id has to be matched before falling back to the list wording.
  if (path.includes('/clients')) {
    if (/\/clients\/\d+/.test(path)) {
      if (path.includes('/companies'))
        return 'Visualizou as empresas do cliente';

      return 'Abriu os dados cliente';
    }

    if (method === 'GET') return 'Visualizou a lista de clientes do Oversight';
  }

  // Os segmentos de ação levam barra para não casar com query string:
  // "?status=pending_consent" contém "end", "?order_by=started_at" contém
  // "start".
  if (path.includes('delegated')) {
    if (path.includes('/start')) return 'Iniciou um acesso delegado';
    if (path.includes('/cancel')) {
      return 'Cancelou a solicitação de acesso delegado';
    }
    if (path.includes('/end')) return 'Encerrou o acesso delegado';
    if (method === 'POST') return 'Criou um acesso delegado';
    if (/\/delegated-sessions\/\d+/.test(path)) {
      return 'Abriu um acesso delegado';
    }
    if (method === 'GET') return 'Visualizou a lista de acessos delegados';
  }

  if (path.includes('sessions') && method === 'GET') {
    return 'Visualizou a lista de sessões';
  }

  if (path.includes('session_events') && method === 'GET') {
    return 'Visualizou a lista de eventos de sessão';
  }

  return undefined;
}

// Caminhos em que o primeiro segmento esconde o recurso: `companies/me/plan`
// não é "dados da empresa". Prefixo mais longo vence, como em routeGroupLabels,
// e cada entrada é uma decisão explícita — nada muda de rótulo sozinho.
const endpointLabels: Record<string, string> = {
  'companies/evaluation-config': 'a configuração de avaliação',
  'companies/me/address': 'o endereço da empresa',
  'companies/me/billing-portal': 'o portal de cobrança',
  'companies/me/default-settings': 'as configurações padrão da empresa',
  'companies/me/hive-requests': 'as solicitações do Hive',
  'companies/me/notice-term-status': 'o termo de edital',
  'companies/me/payment-term-status': 'o termo de pagamento',
  'companies/me/plan': 'o plano da empresa',
  'companies/me/users': 'os usuários da empresa',
  'plans/me': 'o próprio plano',
  'rooms/{id}/pictures': 'as fotos do cômodo',
  'users/{id}': 'dados do usuário',
  'users/me/data': 'os próprios dados',
  'users/me/idwall-number': 'a validação de identidade',
  'users/registration-status': 'o status do cadastro',
};

function describeEndpointResource(endpoint: string): string | undefined {
  const path = endpoint
    .split('?')[0]
    .toLowerCase()
    .replace(/^\/?api\/[^/]+\//, '')
    .replace(/\/\d+(?=\/|$)/g, '/{id}');

  const exact = endpointLabels[path];
  if (exact) return exact;

  const [longest] = Object.keys(endpointLabels)
    .filter((candidate) => path.startsWith(`${candidate}/`))
    .sort((a, b) => b.length - a.length);

  return longest ? endpointLabels[longest] : undefined;
}

function describeSessionEvent(method: string, endpoint: string): string {
  const path = endpoint.toLowerCase();

  const auth = describeAuthEvent(method, path);
  if (auth) return auth;

  const notice = describePublicNoticeEvent(method, path);
  if (notice) return notice;

  const workOrder = describeWorkOrderEvent(method, path);
  if (workOrder) return workOrder;

  if (path.includes('sample')) {
    if (method === 'POST') return 'Criou uma amostra';
    if (method === 'DELETE') return 'Removeu uma amostra';
    if (method === 'PUT' || method === 'PATCH') return 'Atualizou uma amostra';
    return 'Visualizou amostras';
  }

  const form = describeFormEvent(method, path);
  if (form) return form;

  if (path.includes('inspection')) {
    if (method === 'POST') return 'Criou uma vistoria';
    return 'Visualizou vistorias';
  }

  const oversight = describeOversightEvent(method, path);
  if (oversight) return oversight;

  // `/companies/me/credits` cairia no fallback como `companies` ("dados da
  // empresa"), escondendo que a consulta foi ao saldo de créditos.
  if (path.includes('/credits')) return 'Carregou os créditos da empresa';

  const verb = methodVerb[method] ?? 'Acessou';
  const segMatch = endpoint.match(/\/api\/[^/]+\/([^/?]+)/i);
  const key = segMatch ? segMatch[1].toLowerCase() : '';
  const resource =
    describeEndpointResource(endpoint) ??
    resourceLabels[key] ??
    (key ? key.replace(/-/g, ' ') : 'um recurso');

  return `${verb} ${resource}`;
}

// No bootstrap o app só busca dados para montar a tela; escritas mantêm o verbo.
const readVerbPattern = /^(Visualizou|Abriu|Acessou)\b/;

function describeBootstrapEvent(method: string, endpoint: string): string {
  return describeSessionEvent(method, endpoint).replace(
    readVerbPattern,
    'Carregou'
  );
}

// No Início o app busca os dados do usuário e da empresa só para montar a tela.
const homeLoadedResources = [
  endpointLabels['users/{id}'],
  resourceLabels.companies,
].map((resource) => `${methodVerb.GET} ${resource}`);

function describeHomeEvent(method: string, endpoint: string): string {
  const description = describeSessionEvent(method, endpoint);

  return method === 'GET' && homeLoadedResources.includes(description)
    ? describeBootstrapEvent(method, endpoint)
    : description;
}

function buildParams(filters: Filter[], search: string): string {
  const params = new URLSearchParams();

  params.set('is_active', 'true');

  if (search) params.set('search', search);

  filters.forEach((filter) => {
    const activeItem = filter.items.find((item) => item.active);
    if (activeItem?.value && activeItem.value !== 'all') {
      params.set(filter.paramKey, activeItem.value);
    }
  });

  return params.toString();
}

// A coluna "Última sessão" mostra "Online" ou a data em que a última sessão
// terminou, então ela não é monotônica na própria chave: online é o valor mais
// recente da coluna e não está no timestamp — quem está online carrega a data da
// sessão anterior, ou nenhuma. Ordenar só pela chave espalha os online pela
// lista. Espelha o _last_session_terms do backend: mais recente primeiro é
// online, depois ended_at decrescente, e quem nunca teve sessão fica na ponta
// menos recente.
function lastSessionComparator<T>(
  isOnline: (row: T) => boolean,
  getEndedAt: (row: T) => string | null | undefined
): (a: T, b: T, order: 'asc' | 'desc') => number {
  return (a, b, order) => {
    // No cabeçalho, decrescente é "mais recente primeiro" — o inverso do que o
    // mesmo campo significa na API, onde é o ascendente que traz os online.
    const direction = order === 'desc' ? 1 : -1;

    if (isOnline(a) !== isOnline(b)) {
      return (isOnline(a) ? -1 : 1) * direction;
    }

    const endedA = getEndedAt(a);
    const endedB = getEndedAt(b);

    if (!endedA && !endedB) return 0;
    if (!endedA) return direction;
    if (!endedB) return -direction;

    return endedB.localeCompare(endedA) * direction;
  };
}

export type { Filter, Item };
export {
  delegatedAccessLabels,
  describeBootstrapEvent,
  describeHomeEvent,
  describeRouteGroup,
  describeSessionEvent,
  expiredDetail,
  formatDate,
  getErrorTypeTitle,
  getTerminatedByLabel,
  isDeletedUser,
  lastSessionComparator,
  normalizeStr,
  endReasonLabel,
  buildParams,
};
